/// <amd-module name="TFS/Token/PatTokenPage/Components/Common/FutureDatePicker" />
import { Dropdown, IDropdownOption } from "OfficeFabric/Dropdown";
import { Label } from "OfficeFabric/Label";
import { mergeStyleSets } from "OfficeFabric/Styling";
import * as React from 'react';
import * as Resources from "TFS/Token/PatTokenPage/Resources";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageContext } from "VSS/Platform/Context";
import { memoize } from "VSS/Core/Util/Memoize";
import { getId } from "VSS/Core/Util/Object";
import { DatePicker } from "VSSUI/DatePicker";
import { FocusZoneDirection } from "VSSUI/FocusZone";

export const enum TokenMode {
    Create,
    Edit
}

export interface IFutureDatePickerProps {
    date: Date,
    maxDaysNumber?: number,
    dropDownOptions?: { key: string, daysAhead: number }[],
    defaultOptionKey?: string,
    onDateChange: (dateTime: Date) => void
    tokenMode: TokenMode
    validFrom: Date
    tokenLifespanPolicyToggleEnabled: boolean
    pageContext: IVssPageContext
}

export interface IFutureDatePickerState {
    selectedOption?: string,
}

const custumDateKey = "custom";


export default class FutureDatePicker extends React.PureComponent<IFutureDatePickerProps, IFutureDatePickerState> {
    private readonly datePickerGroupId = getId("datePickerGroup");
    private static DEFAULT_MAX_DAYS_IN_THE_FUTURE = 364;

    constructor(props: IFutureDatePickerProps) {
        super(props);

        this.state = {};
    }

    @memoize
    private static getStyles() {
        return mergeStyleSets({
            calenderGroup: {
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
            },
            calenderDropDown: {
                flexGrow: 1,
                marginRight: "12px",
            },
        })
    }

    public render() {
        const { date } = this.props;
        const styles = FutureDatePicker.getStyles();
        const dropDownOptions = FutureDatePicker.createDropdownOptions(this.props.dropDownOptions)
        const minExpirationDate = this.getMinExpirationDate();
        const maxExpirationDate = this.getMaxExpirationDate();
        const selectedKey = this.state.selectedOption || this.props.defaultOptionKey || dropDownOptions[0].key;
        return (
            <>
                <Label htmlFor={this.datePickerGroupId}>
                    {Resources.Expiration + " (UTC)"}
                </Label>
                <div className={styles.calenderGroup} id={this.datePickerGroupId}>
                    <div className={styles.calenderDropDown} >
                        <Dropdown
                            ariaLabel={Resources.Expiration}
                            options={dropDownOptions}
                            selectedKey={selectedKey}
                            onChanged={this.onDropdownChange}
                        />
                    </div>
                    <DatePicker
                        ariaLabel={Resources.SpecificDate}
                        value={date}
                        minDate={minExpirationDate}
                        maxDate={maxExpirationDate}
                        onChange={this.onDatePickerChange}
                        focusZoneProps={{ includeDefaults: true, direction: FocusZoneDirection.Vertical, handleTabKey: true }}
                />
                </div>
            </>
        )
    }

    private onDatePickerChange = (date: Date | null | undefined) => {
        if (!!date) {
            let currentTime = new Date();
            date.setHours(currentTime.getHours(), currentTime.getMinutes(), currentTime.getSeconds(), currentTime.getMilliseconds())
            this.setState({ selectedOption: custumDateKey });
            this.props.onDateChange(date);
        }
    }

    private onDropdownChange = (dropDownOption: IDropdownOption) => {
        if (dropDownOption.key !== custumDateKey && this.props.dropDownOptions) {
            const option = this.props.dropDownOptions.find((opt) => opt.key === dropDownOption.key);
            if (option) {
                const newDate = this.createNewDateDaysLater(new Date(), option.daysAhead);
                this.setState({ selectedOption: dropDownOption.key.toString() });
                this.props.onDateChange(newDate);
            }
        } else {
            // we are chaning to custom from nothing else so just set the key
            this.setState({ selectedOption: dropDownOption.key.toString() });
        }
    }

    @memoize
    private static createDropdownOptions(options?: { key: string, daysAhead: number }[]) {
        let dropDownOptions: { key: string, text: string }[] = (options || []).map((option) => {
            return {
                key: option.key,
                text: option.daysAhead + " " + Resources.Days,
            };
        });;
        dropDownOptions = dropDownOptions.concat({
            key: custumDateKey,
            text: Resources.CustomDefined
        })
        return dropDownOptions;
    }

    private createNewDateDaysLater = function (date: Date, days: number): Date {
        const newDate = new Date(date.getTime());
        newDate.setDate(date.getDate() + days);
        return newDate;
    };

    private getMinExpirationDate = (): Date => {
        if (isFeatureFlagEnabled(this.props.pageContext, "VisualStudio.Services.TenantPolicy.UseFixedPersonalAccessTokenExpirationDate", false)) {
            return this.createNewDateDaysLater(new Date(), 1);
        }
        return this.createNewDateDaysLater(new Date(), 0);
    };

    private getMaxExpirationDate = (): Date => {
        var startDate = new Date();
        if (isFeatureFlagEnabled(this.props.pageContext, "VisualStudio.Services.TenantPolicy.UseFixedPersonalAccessTokenExpirationDate", false) &&
            isFeatureFlagEnabled(this.props.pageContext, "VisualStudio.Services.TenantPolicy.Admin.PATs.RestrictTokenLifespanPolicy", false) &&
            this.props.tokenLifespanPolicyToggleEnabled === true) {
            if (this.props.tokenMode == TokenMode.Edit) {
                startDate = this.props.validFrom
            }
        }
        return this.createNewDateDaysLater(startDate, this.props.maxDaysNumber ?? FutureDatePicker.DEFAULT_MAX_DAYS_IN_THE_FUTURE);
    };
}
